import { useGlobalMetricsHealth } from '@/hooks/useGlobalMetricsHealth'
import { StatusPill } from '@/components/infrastructure/shared/StatusPill'
import { Card } from '@/components/ui/card'
import { TrendingUp, Rocket, Users, Coins } from 'lucide-react'

function formatUsd(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toFixed(2)}`
}

function freshness(syncedAt: string | null): { status: 'ok' | 'warning' | 'error'; label: string } {
  if (!syncedAt) return { status: 'error', label: 'never synced' }
  const ageMs = Date.now() - new Date(syncedAt).getTime()
  if (ageMs < 10 * 60 * 1000) return { status: 'ok', label: `${Math.round(ageMs / 1000)}s ago` }
  if (ageMs < 60 * 60 * 1000) return { status: 'warning', label: `${Math.round(ageMs / 60_000)}m ago` }
  return { status: 'error', label: `${Math.round(ageMs / 3_600_000)}h ago` }
}

export function GlobalMetricsPage() {
  const { data, isLoading, error } = useGlobalMetricsHealth()

  if (isLoading) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Global Metrics</h2>
          <p className="text-sm sm:text-base text-muted-foreground">Loading…</p>
        </div>
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Global Metrics</h2>
          <p className="text-sm sm:text-base text-status-error">
            Error loading: {(error as Error)?.message ?? 'unknown'}
          </p>
        </div>
      </div>
    )
  }

  const { stats, perPool } = data
  const dammV2Fresh = freshness(stats.dammV2SyncedAt ?? null)
  const anyDrift = perPool.some((p) => p.status === 'error')

  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold">Global Metrics</h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Homepage metrics with per-venue breakdown + DAMM V2 reconciliation against indexer.
        </p>
      </div>

      {/* 4-tile homepage banner */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <MetricTile
          icon={TrendingUp}
          title="Volume"
          total={formatUsd(stats.volume?.totalUsd ?? stats.totalVolume)}
          breakdown={
            stats.volume
              ? `DBC ${formatUsd(stats.volume.dbcUsd)} · DAMM V2 ${formatUsd(stats.volume.dammV2Usd)}`
              : null
          }
          status={dammV2Fresh.status}
          statusLabel={`damm v2 ${dammV2Fresh.label}`}
        />
        <MetricTile
          icon={Rocket}
          title="Launches"
          total={String(stats.launches?.count ?? stats.tokensLaunched)}
          breakdown={
            stats.launches
              ? `${stats.launches.graduatedCount} graduated`
              : null
          }
          status="ok"
          statusLabel="all-time"
        />
        <MetricTile
          icon={Users}
          title="Traders"
          total={String(stats.activeUsers)}
          breakdown="distinct wallets"
          status="ok"
          statusLabel="all-time"
        />
        <MetricTile
          icon={Coins}
          title="Creator Earnings"
          total={formatUsd(stats.creatorEarnings?.totalUsd ?? stats.creatorEarningsUsd)}
          breakdown={
            stats.creatorEarnings
              ? `DBC ${formatUsd(stats.creatorEarnings.dbcUsd)} · DAMM V2 ${formatUsd(stats.creatorEarnings.dammV2Usd)}`
              : null
          }
          status={dammV2Fresh.status}
          statusLabel={`damm v2 ${dammV2Fresh.label}`}
        />
      </div>

      {/* Source breakdown */}
      <Card className="p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold mb-3">Source breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left py-2 pr-4">Metric</th>
                <th className="text-left py-2 pr-4">Source</th>
                <th className="text-right py-2 pr-4">Value</th>
                <th className="text-left py-2">Last updated</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              <tr className="border-b border-border/40">
                <td className="py-2 pr-4">Volume DBC</td>
                <td className="py-2 pr-4 text-muted-foreground">SUM(pool_stats.cumulative_volume_usd)</td>
                <td className="py-2 pr-4 text-right">{formatUsd(stats.volume?.dbcUsd ?? 0)}</td>
                <td className="py-2 text-muted-foreground">sync-pool-stats / 1 min</td>
              </tr>
              <tr className="border-b border-border/40">
                <td className="py-2 pr-4">Volume DAMM V2</td>
                <td className="py-2 pr-4 text-muted-foreground">SUM(pool_stats.damm_v2_cumulative_volume_usd_onchain)</td>
                <td className="py-2 pr-4 text-right">{formatUsd(stats.volume?.dammV2Usd ?? 0)}</td>
                <td className="py-2"><StatusPill status={dammV2Fresh.status} label={dammV2Fresh.label} /></td>
              </tr>
              <tr className="border-b border-border/40">
                <td className="py-2 pr-4">Creator Earnings DBC</td>
                <td className="py-2 pr-4 text-muted-foreground">SUM(pool_stats.cumulative_creator_earnings_usd)</td>
                <td className="py-2 pr-4 text-right">{formatUsd(stats.creatorEarnings?.dbcUsd ?? 0)}</td>
                <td className="py-2 text-muted-foreground">sync-pool-stats / 1 min</td>
              </tr>
              <tr className="border-b border-border/40">
                <td className="py-2 pr-4">Creator Earnings DAMM V2</td>
                <td className="py-2 pr-4 text-muted-foreground">SUM(pool_stats.damm_v2_cumulative_fee_usd)</td>
                <td className="py-2 pr-4 text-right">{formatUsd(stats.creatorEarnings?.dammV2Usd ?? 0)}</td>
                <td className="py-2"><StatusPill status={dammV2Fresh.status} label={dammV2Fresh.label} /></td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Traders</td>
                <td className="py-2 pr-4 text-muted-foreground">RPC count_distinct_users(user_sessions)</td>
                <td className="py-2 pr-4 text-right">{stats.activeUsers}</td>
                <td className="py-2 text-muted-foreground">live</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Per-pool reconciliation */}
      <Card className="p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base sm:text-lg font-semibold">DAMM V2 indexer reconciliation</h3>
          {anyDrift && (
            <StatusPill status="error" label="indexer drift detected" />
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          On-chain volume = lifetime totalLpBFee / lpEffectiveRate (source of truth, byte-decoded
          from cp-amm Pool account). DB volume = SUM(quote_amount) on market-data trades for the
          same DAMM V2 pool address. Capture rate &lt; 80% = indexer dropping swaps; investigate.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left py-2 pr-4">Symbol</th>
                <th className="text-left py-2 pr-4">Mint</th>
                <th className="text-right py-2 pr-4">On-chain (USD)</th>
                <th className="text-right py-2 pr-4">DB (SOL)</th>
                <th className="text-right py-2 pr-4">DB (USD)</th>
                <th className="text-right py-2 pr-4">Capture %</th>
                <th className="text-left py-2">Status</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {perPool.map((row) => (
                <tr key={row.dammPoolAddress} className="border-b border-border/40">
                  <td className="py-2 pr-4 font-semibold">{row.symbol}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{row.mintAddress.slice(0, 8)}…</td>
                  <td className="py-2 pr-4 text-right">{formatUsd(row.onchainVolumeUsd)}</td>
                  <td className="py-2 pr-4 text-right">{row.dbVolumeSol.toFixed(2)}</td>
                  <td className="py-2 pr-4 text-right">{formatUsd(row.dbVolumeUsd)}</td>
                  <td className="py-2 pr-4 text-right">{(row.captureRate * 100).toFixed(1)}%</td>
                  <td className="py-2"><StatusPill status={row.status} label={row.status === 'ok' ? '≥ 95%' : row.status === 'warning' ? '80-95%' : '< 80%'} /></td>
                </tr>
              ))}
              {perPool.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-4 text-center text-muted-foreground">No graduated coins yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

function MetricTile({
  icon: Icon,
  title,
  total,
  breakdown,
  status,
  statusLabel,
}: {
  icon: typeof TrendingUp
  title: string
  total: string
  breakdown: string | null
  status: 'ok' | 'warning' | 'error'
  statusLabel: string
}) {
  return (
    <Card className="p-3 sm:p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-primary" />
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
      </div>
      <div className="text-lg sm:text-2xl font-bold mb-1">{total}</div>
      {breakdown && <div className="text-[10px] sm:text-xs text-muted-foreground mb-2">{breakdown}</div>}
      <StatusPill status={status} label={statusLabel} />
    </Card>
  )
}
